import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminManagementService } from '../../services/adminService';
import { Search, Plus, Trash2, Power, Edit2 } from 'lucide-react';
import Pagination from '../../components/admin/Pagination';

function AdminCoupons() {
    const [searchParams, setSearchParams] = useSearchParams();
    const currentPage = parseInt(searchParams.get('page')) || 1;

    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCoupon, setEditingCoupon] = useState(null);
    const [formData, setFormData] = useState({
        code: '',
        description: '',
        discountType: 'percentage',
        discountValue: 0,
        currency: 'USD',
        durationType: 'one_time',
        durationCycles: '',
        maxRedemptions: '',
        expiresAt: '',
        applicablePlans: []
    });

    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        loadCoupons();
    }, [currentPage, debouncedSearch]);

    const loadCoupons = async () => {
        try {
            setLoading(true);
            const response = await adminManagementService.getCoupons({
                search: debouncedSearch,
                page: currentPage
            });
            setCoupons(response);
        } catch (error) {
            console.error('Failed to load coupons', error);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenCreate = () => {
        setEditingCoupon(null);
        setFormData({
            code: '',
            description: '',
            discountType: 'percentage',
            discountValue: 0,
            currency: 'USD',
            durationType: 'one_time',
            durationCycles: '',
            maxRedemptions: '',
            expiresAt: '',
            applicablePlans: []
        });
        setError(null);
        setSuccess(null);
        setIsModalOpen(true);
    };

    const handleOpenEdit = (coupon) => {
        setEditingCoupon(coupon);
        setFormData({
            code: coupon.code,
            description: coupon.description || '',
            discountType: coupon.discountType,
            discountValue: coupon.discountValue,
            currency: coupon.currency,
            durationType: coupon.durationType,
            durationCycles: coupon.durationCycles || '',
            maxRedemptions: coupon.maxRedemptions || '',
            expiresAt: coupon.expiresAt ? new Date(coupon.expiresAt).toISOString().split('T')[0] : '',
            applicablePlans: coupon.applicablePlans || []
        });
        setError(null);
        setSuccess(null);
        setIsModalOpen(true);
    };

    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox' && name === 'applicablePlans') {
            const plan = value;
            setFormData(prev => ({
                ...prev,
                applicablePlans: checked
                    ? [...prev.applicablePlans, plan]
                    : prev.applicablePlans.filter(p => p !== plan)
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        setError(null);
        try {
            const payload = {
                ...formData,
                discountValue: parseFloat(formData.discountValue),
                durationCycles: formData.durationCycles ? parseInt(formData.durationCycles) : null,
                maxRedemptions: formData.maxRedemptions ? parseInt(formData.maxRedemptions) : null,
                expiresAt: formData.expiresAt || null
            };

            if (editingCoupon) {
                await adminManagementService.updateCoupon(editingCoupon.id, payload);
                setSuccess('Coupon updated successfully');
            } else {
                await adminManagementService.createCoupon(payload);
                setSuccess('Coupon created successfully');
            }

            await loadCoupons();
            setTimeout(() => setIsModalOpen(false), 1500);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to save coupon');
        } finally {
            setIsSaving(false);
        }
    };

    const handleToggleActive = async (id) => {
        try {
            await adminManagementService.toggleCouponActive(id);
            await loadCoupons();
        } catch (error) {
            console.error('Failed to toggle coupon status', error);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this coupon? This cannot be undone.')) return;
        try {
            await adminManagementService.deleteCoupon(id);
            await loadCoupons();
        } catch (error) {
            console.error('Failed to delete coupon', error);
        }
    };

    if (loading && coupons.length === 0) return <div className="admin-loading">Loading coupons...</div>;

    return (
        <div className="admin-page">
            <div className="admin-page-header">
                <h2>Coupon Management ({coupons.length})</h2>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <div className="admin-search-bar">
                        <Search size={18} />
                        <input
                            type="text"
                            placeholder="Search code..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button className="admin-add-btn btn btn-primary" onClick={handleOpenCreate}>
                        <Plus size={18} />
                        Create Coupon
                    </button>
                </div>
            </div>

            <div className="admin-table-container">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Code</th>
                            <th>Description</th>
                            <th>Discount</th>
                            <th>Duration</th>
                            <th>Usage</th>
                            <th>Status</th>
                            <th>Expires</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {coupons.map(coupon => (
                            <tr key={coupon.id} style={{ opacity: coupon.isActive ? 1 : 0.6 }}>
                                <td>
                                    <strong>{coupon.code}</strong>
                                </td>
                                <td>{coupon.description || '-'}</td>
                                <td>
                                    {coupon.discountType === 'percentage'
                                        ? `${coupon.discountValue}%`
                                        : `$${coupon.discountValue} ${coupon.currency}`}
                                </td>
                                <td>
                                    <span style={{ textTransform: 'capitalize' }}>
                                        {coupon.durationType.replace('_', ' ')}
                                        {coupon.durationType === 'limited_cycles' && ` (${coupon.durationCycles} cycles)`}
                                    </span>
                                </td>
                                <td>
                                    {coupon.redemptionsCount} / {coupon.maxRedemptions || '∞'}
                                </td>
                                <td>
                                    <span className={`status-badge ${coupon.isActive ? 'success' : 'warning'}`}>
                                        {coupon.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td>
                                    {coupon.expiresAt ? new Date(coupon.expiresAt).toLocaleDateString() : 'Never'}
                                </td>
                                <td>
                                    <div className="admin-actions">
                                        <button className="icon-btn" onClick={() => handleOpenEdit(coupon)} title="Edit">
                                            <Edit2 size={16} />
                                        </button>
                                        <button
                                            className={`icon-btn ${coupon.isActive ? 'warning-text' : 'success-text'}`}
                                            onClick={() => handleToggleActive(coupon.id)}
                                            title={coupon.isActive ? 'Deactivate' : 'Activate'}
                                        >
                                            <Power size={16} />
                                        </button>
                                        <button className="icon-btn danger-text" onClick={() => handleDelete(coupon.id)} title="Delete">
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {coupons.length === 0 && (
                            <tr>
                                <td colSpan="8" style={{ textAlign: 'center', padding: '2rem' }}>
                                    No coupons found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Create/Edit Modal */}
            {isModalOpen && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal">
                        <div className="admin-modal-header">
                            <h3>{editingCoupon ? `Edit Coupon: ${editingCoupon.code}` : 'Create New Coupon'}</h3>
                            <button className="close-btn" onClick={() => setIsModalOpen(false)}>×</button>
                        </div>
                        <form onSubmit={handleSave} className="admin-modal-body">
                            {error && <div className="admin-form-error">{error}</div>}
                            {success && <div className="admin-form-success">{success}</div>}

                            <div className="form-section">
                                <div className="form-group">
                                    <label>Coupon Code</label>
                                    <input
                                        type="text"
                                        name="code"
                                        placeholder="e.g. SAVE50"
                                        value={formData.code}
                                        onChange={handleFormChange}
                                        disabled={!!editingCoupon}
                                        required
                                        style={{ textTransform: 'uppercase' }}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Description</label>
                                    <input
                                        type="text"
                                        name="description"
                                        placeholder="Internal only (e.g. Winter Sale 2024)"
                                        value={formData.description}
                                        onChange={handleFormChange}
                                    />
                                </div>
                            </div>

                            <div className="form-section">
                                <h4>Discount Rules</h4>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Type</label>
                                        <select name="discountType" value={formData.discountType} onChange={handleFormChange}>
                                            <option value="percentage">Percentage (%)</option>
                                            <option value="fixed">Fixed Amount</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Value</label>
                                        <input
                                            type="number"
                                            name="discountValue"
                                            value={formData.discountValue}
                                            onChange={handleFormChange}
                                            min="0"
                                            step="0.01"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-section">
                                <h4>Duration & Limits</h4>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Duration Type</label>
                                        <select name="durationType" value={formData.durationType} onChange={handleFormChange}>
                                            <option value="one_time">One-time (Checkout only)</option>
                                            <option value="forever">Forever (Recurring)</option>
                                            <option value="limited_cycles">Limited Cycles</option>
                                        </select>
                                    </div>
                                    {formData.durationType === 'limited_cycles' && (
                                        <div className="form-group">
                                            <label>Cycles</label>
                                            <input
                                                type="number"
                                                name="durationCycles"
                                                value={formData.durationCycles}
                                                onChange={handleFormChange}
                                                min="1"
                                                required
                                            />
                                        </div>
                                    )}
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Max Global Redemptions</label>
                                        <input
                                            type="number"
                                            name="maxRedemptions"
                                            placeholder="Leave empty for unlimited"
                                            value={formData.maxRedemptions}
                                            onChange={handleFormChange}
                                            min="1"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Expires At</label>
                                        <input
                                            type="date"
                                            name="expiresAt"
                                            value={formData.expiresAt}
                                            onChange={handleFormChange}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-section">
                                <h4>Applicable Plans</h4>
                                <div className="plans-checklist" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                    {['basic', 'pro', 'business', 'enterprise'].map(plan => (
                                        <div key={plan} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <input
                                                type="checkbox"
                                                id={`plan-${plan}`}
                                                name="applicablePlans"
                                                value={plan}
                                                checked={formData.applicablePlans.includes(plan)}
                                                onChange={handleFormChange}
                                            />
                                            <label htmlFor={`plan-${plan}`} style={{ marginBottom: 0, textTransform: 'capitalize' }}>
                                                {plan}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                                <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '10px' }}>
                                    If none selected, coupon applies to all plans.
                                </p>
                            </div>

                            <div className="admin-modal-footer">
                                <button type="button" className="admin-cancel-btn" onClick={() => setIsModalOpen(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="admin-save-btn" disabled={isSaving}>
                                    {isSaving ? 'Processing...' : (editingCoupon ? 'Save Changes' : 'Create Coupon')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminCoupons;
